import { ChevronLeft, FileText, File, FileSpreadsheet, Plus, Upload, Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate, useParams } from "react-router-dom";
import { BottomNav } from "@/components/BottomNav";
import { useState, useRef } from "react";
import { useKnowledge } from "@/hooks/useKnowledge";

const ProjectKnowledge = () => {
  const navigate = useNavigate();
  const { colonyId } = useParams<{ colonyId: string }>();
  const [searchTerm, setSearchTerm] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { query, upload, del } = useKnowledge(colonyId || "");
  const files = (query.data?.items || []).map((d: any) => ({
    id: d.id || d.docId || d.fileKey,
    title: d.title || d.filename || d.name,
    filename: d.filename || d.title,
    mime: d.mime || d.type || 'application/octet-stream',
    bytes: d.bytes || d.size || 0,
    createdAt: d.uploadedAt || d.createdAt || new Date().toISOString(),
  }));

  const filteredFiles = files.filter(file => 
    (file.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (file.filename || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getFileIcon = (mime: string) => {
    if (mime?.includes('pdf')) return FileText;
    if (mime?.includes('text')) return File;
    if (mime?.includes('spreadsheet') || mime?.includes('excel')) return FileSpreadsheet;
    if (mime?.includes('document') || mime?.includes('word')) return FileText;
    return File;
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes) return '—';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString();
  };

  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) upload.mutate(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="min-h-screen bg-background safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-ios-separator bg-ios-surface">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate("/")}
          className="text-foreground hover:bg-ios-surface-secondary w-11 h-11 rounded-full"
        >
          <ChevronLeft size={22} />
        </Button>
        <h1 className="ios-title text-foreground">Knowledge</h1>
        <Button 
          variant="ghost" 
          size="icon"
          onClick={handleFileUpload}
          className="text-nav-active hover:bg-ios-surface-secondary w-11 h-11 rounded-full"
        >
          <Plus size={22} />
        </Button>
      </div>

      {/* Search Bar */}
      <div className="px-4 py-3 bg-ios-surface border-b border-ios-separator">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search files..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-ios-surface-secondary border-ios-separator rounded-xl h-9 text-sm"
          />
        </div>
      </div>

      {/* Upload Section */}
      <div className="px-4 py-3 bg-ios-surface border-b border-ios-separator">
        <Button
          onClick={handleFileUpload}
          disabled={upload.isPending}
          className="w-full bg-ios-blue text-white hover:bg-ios-blue/90 rounded-xl h-12 font-medium disabled:opacity-50"
        >
          <Upload size={20} className="mr-2" />
          {upload.isPending ? 'Uploading...' : 'Upload Files'}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          className="hidden"
          accept=".pdf,.txt,.docx,.xlsx,.doc,.xls"
        />
      </div>

      {/* File List */}
      <div className="flex-1 bg-ios-surface">
        {query.isLoading ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ios-blue"></div>
            <p className="text-muted-foreground mt-4">Loading files...</p>
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <FileText size={48} className="text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              {searchTerm ? "No files match your search" : "No files uploaded yet"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-ios-separator">
            {filteredFiles.map((file) => {
              const IconComponent = getFileIcon(file.mime || '');
              return (
                <div
                  key={file.id}
                  className="ios-list-item flex items-center px-4 py-3"
                >
                  <div className="flex items-center flex-1 min-w-0">
                    <div className="flex-shrink-0 mr-3">
                      <IconComponent size={24} className="text-ios-blue" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-foreground font-medium truncate">{file.title || file.filename || 'Untitled'}</div>
                      <div className="text-muted-foreground text-sm">
                        {formatFileSize(file.bytes)} • {formatDate(file.createdAt)}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => del.mutate({ docId: file.id })}
                    disabled={del.isPending}
                    className="text-red-500 hover:bg-red-50 hover:text-red-600 w-8 h-8 ml-2"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default ProjectKnowledge;